import { Card, Progress, Typography } from 'antd';
import parse from 'html-react-parser';
import Nudge from '../../../../Models/Nudge';
import styles from './index.module.css';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const getTitleElement = (label: string) => {
  return (
    <Typography.Text
      style={{
        fontSize: '18px',
        width: '100%',
        margin: 0,
        fontWeight: '600',
        color: '#fff',
        textWrap: 'wrap',
      }}
    >
      {parse(label, { trim: false })}
    </Typography.Text>
  );
};

const getTitle = (id: string) => {
  switch (id) {
    case '1':
      return getTitleElement('Your Spending Trend');
    case '2':
      return getTitleElement('Savings Tips');
    case '3':
      return getTitleElement('Goal Progress');
    case '4':
      return getTitleElement('Invest in Mutual Funds');
    case '5':
      return getTitleElement('Recurring Payments');
    case '6':
      return getTitleElement('Invest in Fixed Deposit');
    case '7':
      return getTitleElement('Income vs Expense');
    case '8':
      return getTitleElement('Invest in Stocks');
    case '9':
      return getTitleElement('Food Spending Analysis');
    case '10':
      return getTitleElement('Power of SIP');
    case '11':
      return getTitleElement('Discretionary Spending');
    case '12':
      return getTitleElement('Split Investment Strategy');
    case '13':
      return getTitleElement('Upcoming Bills');
    case '14':
      return getTitleElement('EMI Overview');
    case '15':
      return getTitleElement('50-30-20 Budget Rule');
    case '16':
      return getTitleElement('Peer MF Investment');
    case '17':
      return getTitleElement('Peer Savings Rate');
    case '18':
      return getTitleElement('Emergency Fund');
    case '19':
      return getTitleElement('Dining Comparison');
    case '20':
      return getTitleElement('Peer Stock Allocation');
    default:
      return getTitleElement('Financial Insight');
  }
};

const getDetails = (id: string) => {
  switch (id) {
    case '1':
      // Spending trend chart
      const spendingData = [
        { month: 'Jul', amount: 45000 },
        { month: 'Aug', amount: 52000 },
        { month: 'Sep', amount: 48000 },
        { month: 'Oct', amount: 55000 },
        { month: 'Nov', amount: 42000 },
        { month: 'Dec', amount: 39600 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ fontSize: 14, color: '#666' }}>
              Your spending decreased by <strong style={{ color: '#52c41a' }}>12%</strong> this month
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <AreaChart data={spendingData}>
              <defs>
                <linearGradient id='colorSpend' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#52c41a' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#52c41a' stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis dataKey='month' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v / 1000}K`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Area type='monotone' dataKey='amount' stroke='#52c41a' fill='url(#colorSpend)' />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: 12, color: '#888', fontSize: 12 }}>
            Keep up the good work! You're on track to save more.
          </div>
        </>
      );

    case '2':
      // Savings tips with progress
      return (
        <>
          <div style={{ padding: '10px 0' }}>
            <Typography.Text strong>The 20% Rule</Typography.Text>
            <Typography.Paragraph style={{ color: '#666', marginTop: 8 }}>
              Financial experts recommend saving at least 20% of your income before spending on anything else.
            </Typography.Paragraph>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Your current savings rate</span>
                <span style={{ fontWeight: 600, color: '#52c41a' }}>18%</span>
              </div>
              <Progress percent={90} strokeColor='#52c41a' showInfo={false} />
              <div style={{ textAlign: 'center', marginTop: 8, color: '#888', fontSize: 12 }}>
                Just 2% more to reach the recommended 20%!
              </div>
            </div>
          </div>
        </>
      );

    case '3':
      // Goal progress
      const goalData = [
        { name: 'Saved', value: 65 },
        { name: 'Remaining', value: 35 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center' }}>
            <ResponsiveContainer width='100%' height={180}>
              <PieChart>
                <Pie
                  data={goalData}
                  innerRadius={50}
                  outerRadius={70}
                  dataKey='value'
                  startAngle={90}
                  endAngle={-270}
                >
                  <Cell fill='#52c41a' />
                  <Cell fill='#f0f0f0' />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ marginTop: -60, marginBottom: 20 }}>
              <Typography.Text style={{ fontSize: 24, fontWeight: 600 }}>65%</Typography.Text>
              <br />
              <Typography.Text style={{ color: '#888' }}>Complete</Typography.Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 20 }}>
              <div>
                <Typography.Text style={{ color: '#888', fontSize: 12 }}>Saved</Typography.Text>
                <br />
                <Typography.Text strong>₹97,500</Typography.Text>
              </div>
              <div>
                <Typography.Text style={{ color: '#888', fontSize: 12 }}>Target</Typography.Text>
                <br />
                <Typography.Text strong>₹1,50,000</Typography.Text>
              </div>
              <div>
                <Typography.Text style={{ color: '#888', fontSize: 12 }}>ETA</Typography.Text>
                <br />
                <Typography.Text strong>8 months</Typography.Text>
              </div>
            </div>
          </div>
        </>
      );

    case '4':
      // Mutual Funds - Networth comparison
      const mfNetworthData = [
        { year: 'Now', withoutInvesting: 500000, withMF: 500000 },
        { year: '2Y', withoutInvesting: 560000, withMF: 620000 },
        { year: '5Y', withoutInvesting: 650000, withMF: 890000 },
        { year: '10Y', withoutInvesting: 800000, withMF: 1580000 },
        { year: '15Y', withoutInvesting: 950000, withMF: 2850000 },
        { year: '20Y', withoutInvesting: 1100000, withMF: 5200000 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Investing <strong>₹25,000/month</strong> in Mutual Funds @ 12% returns
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>Without Investing</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#52c41a', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>With Mutual Funds</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <LineChart data={mfNetworthData}>
              <XAxis dataKey='year' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Line type='monotone' dataKey='withoutInvesting' stroke='#ff4d4f' strokeWidth={2} dot={{ r: 4 }} name='Without Investing' />
              <Line type='monotone' dataKey='withMF' stroke='#52c41a' strokeWidth={3} dot={{ r: 4 }} name='With Mutual Funds' />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#fff1f0', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Without Investing</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#ff4d4f' }}>₹11L</Typography.Text>
            </div>
            <div style={{ background: '#f6ffed', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>With Mutual Funds</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#52c41a' }}>₹52L</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#52c41a', fontWeight: 600 }}>
              Potential gain: ₹41 Lakhs in 20 years!
            </Typography.Text>
          </div>
        </>
      );

    case '5':
      // Recurring payments
      const recurringData = [
        { name: 'Netflix', amount: 649, due: 'Tomorrow' },
        { name: 'Electricity', amount: 2500, due: 'In 3 days' },
        { name: 'Internet', amount: 1200, due: 'In 5 days' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ fontSize: 20, fontWeight: 600 }}>3 Payments</Typography.Text>
            <br />
            <Typography.Text style={{ color: '#888' }}>due this week</Typography.Text>
          </div>
          {recurringData.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <Typography.Text strong>{item.name}</Typography.Text>
                <br />
                <Typography.Text style={{ color: '#fa8c16', fontSize: 12 }}>{item.due}</Typography.Text>
              </div>
              <Typography.Text strong>₹{item.amount.toLocaleString('en-IN')}</Typography.Text>
            </div>
          ))}
          <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              Total upcoming: <strong>₹4,349</strong>
            </Typography.Text>
          </div>
        </>
      );

    case '6':
      // Fixed Deposit - Networth comparison
      const fdNetworthData = [
        { year: 'Now', withoutInvesting: 500000, withFD: 500000 },
        { year: '1Y', withoutInvesting: 515000, withFD: 536000 },
        { year: '2Y', withoutInvesting: 530000, withFD: 574000 },
        { year: '3Y', withoutInvesting: 545000, withFD: 615000 },
        { year: '5Y', withoutInvesting: 575000, withFD: 710000 },
        { year: '10Y', withoutInvesting: 650000, withFD: 1010000 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Investing <strong>₹50,000</strong> in FD @ 7.2% p.a.
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>In Savings (3.5%)</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#2f54eb', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>In Fixed Deposit</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <LineChart data={fdNetworthData}>
              <XAxis dataKey='year' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Line type='monotone' dataKey='withoutInvesting' stroke='#ff4d4f' strokeWidth={2} dot={{ r: 4 }} name='In Savings' />
              <Line type='monotone' dataKey='withFD' stroke='#2f54eb' strokeWidth={3} dot={{ r: 4 }} name='In Fixed Deposit' />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#fff1f0', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Savings Account</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#ff4d4f' }}>₹6.5L</Typography.Text>
            </div>
            <div style={{ background: '#e6f4ff', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Fixed Deposit</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#2f54eb' }}>₹10.1L</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#2f54eb', fontWeight: 600 }}>
              Extra earnings: ₹3.6 Lakhs in 10 years!
            </Typography.Text>
          </div>
        </>
      );

    case '7':
      // Income vs Expense
      const incomeExpenseData = [
        { month: 'Jul', income: 85000, expense: 62000 },
        { month: 'Aug', income: 85000, expense: 58000 },
        { month: 'Sep', income: 90000, expense: 65000 },
        { month: 'Oct', income: 85000, expense: 60000 },
        { month: 'Nov', income: 88000, expense: 55000 },
        { month: 'Dec', income: 92000, expense: 58000 },
      ];
      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16, textAlign: 'center' }}>
            <div>
              <div style={{ width: 12, height: 12, background: '#52c41a', borderRadius: 2, display: 'inline-block', marginRight: 6 }} />
              <Typography.Text style={{ fontSize: 12 }}>Income</Typography.Text>
            </div>
            <div>
              <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2, display: 'inline-block', marginRight: 6 }} />
              <Typography.Text style={{ fontSize: 12 }}>Expense</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <BarChart data={incomeExpenseData}>
              <XAxis dataKey='month' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v / 1000}K`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Bar dataKey='income' fill='#52c41a' radius={[4, 4, 0, 0]} />
              <Bar dataKey='expense' fill='#ff4d4f' radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: 12, color: '#52c41a', fontWeight: 600 }}>
            Ratio: 1.4x - You're living within your means! ✓
          </div>
        </>
      );

    case '8':
      // Stocks - Networth comparison
      const stockNetworthData = [
        { year: 'Now', withoutInvesting: 500000, withStocks: 500000 },
        { year: '2Y', withoutInvesting: 560000, withStocks: 650000 },
        { year: '5Y', withoutInvesting: 650000, withStocks: 1050000 },
        { year: '10Y', withoutInvesting: 800000, withStocks: 2200000 },
        { year: '15Y', withoutInvesting: 950000, withStocks: 4600000 },
        { year: '20Y', withoutInvesting: 1100000, withStocks: 9600000 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Investing surplus in Stocks @ 15% avg returns
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>Without Investing</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#eb2f96', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>With Stocks</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <LineChart data={stockNetworthData}>
              <XAxis dataKey='year' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Line type='monotone' dataKey='withoutInvesting' stroke='#ff4d4f' strokeWidth={2} dot={{ r: 4 }} name='Without Investing' />
              <Line type='monotone' dataKey='withStocks' stroke='#eb2f96' strokeWidth={3} dot={{ r: 4 }} name='With Stocks' />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#fff1f0', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Without Investing</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#ff4d4f' }}>₹11L</Typography.Text>
            </div>
            <div style={{ background: '#fff0f6', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>With Stocks</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#eb2f96' }}>₹96L</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#eb2f96', fontWeight: 600 }}>
              Potential gain: ₹85 Lakhs in 20 years!
            </Typography.Text>
          </div>
          <div style={{ background: '#fff7e6', padding: 8, borderRadius: 8, marginTop: 12, textAlign: 'center' }}>
            <Typography.Text style={{ fontSize: 11, color: '#d48806' }}>
              ⚠️ Stocks are subject to market risks. Past performance doesn't guarantee future returns.
            </Typography.Text>
          </div>
        </>
      );

    case '9':
      // Food spending
      const foodData = [
        { name: 'Dining Out', value: 4500, color: '#FF6B6B' },
        { name: 'Groceries', value: 2500, color: '#4ECDC4' },
        { name: 'Delivery', value: 1000, color: '#45B7D1' },
      ];
      return (
        <>
          <ResponsiveContainer width='100%' height={180}>
            <PieChart>
              <Pie data={foodData} dataKey='value' nameKey='name' cx='50%' cy='50%' innerRadius={40} outerRadius={60}>
                {foodData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Typography.Text>
              Total: <strong>₹8,000</strong> | Budget: <strong>₹6,500</strong>
            </Typography.Text>
            <Progress percent={123} strokeColor='#ff4d4f' showInfo={false} style={{ marginTop: 8 }} />
            <Typography.Text style={{ color: '#ff4d4f', fontSize: 12 }}>23% over budget</Typography.Text>
          </div>
          <div style={{ background: '#fff1f0', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              💡 <strong>Tip:</strong> Try cooking at home 2 more days/week to save ₹2,000
            </Typography.Text>
          </div>
        </>
      );

    case '10':
      // SIP Power - Networth comparison
      const sipNetworthData = [
        { year: 'Now', withoutSIP: 500000, withSIP: 500000 },
        { year: '5Y', withoutSIP: 530000, withSIP: 915000 },
        { year: '10Y', withoutSIP: 560000, withSIP: 1650000 },
        { year: '15Y', withoutSIP: 590000, withSIP: 2900000 },
        { year: '20Y', withoutSIP: 620000, withSIP: 5000000 },
        { year: '25Y', withoutSIP: 650000, withSIP: 8500000 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              SIP of just <strong>₹5,000/month</strong> @ 12% returns
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#ff4d4f', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>Without SIP</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, background: '#52c41a', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 12 }}>With SIP</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <AreaChart data={sipNetworthData}>
              <defs>
                <linearGradient id='colorSIP' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#52c41a' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#52c41a' stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis dataKey='year' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Area type='monotone' dataKey='withoutSIP' stroke='#ff4d4f' fill='#fff1f0' strokeWidth={2} name='Without SIP' />
              <Area type='monotone' dataKey='withSIP' stroke='#52c41a' fill='url(#colorSIP)' strokeWidth={3} name='With SIP' />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#fff1f0', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Without SIP</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#ff4d4f' }}>₹6.5L</Typography.Text>
            </div>
            <div style={{ background: '#f6ffed', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>With SIP</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#52c41a' }}>₹85L</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#52c41a', fontWeight: 600 }}>
              Just ₹5,000/month can grow to ₹85 Lakhs!
            </Typography.Text>
          </div>
        </>
      );

    case '11':
      // Discretionary spending
      const discretionaryData = [
        { category: 'Needs', value: 50, amount: 45000, color: '#52c41a' },
        { category: 'Wants', value: 30, amount: 27000, color: '#1890ff' },
        { category: 'Savings', value: 20, amount: 18000, color: '#722ed1' },
      ];
      return (
        <>
          <ResponsiveContainer width='100%' height={160}>
            <BarChart data={discretionaryData} layout='vertical'>
              <XAxis type='number' tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
              <YAxis dataKey='category' type='category' tick={{ fontSize: 12 }} width={60} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey='value' radius={[0, 4, 4, 0]}>
                {discretionaryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 16 }}>
            {discretionaryData.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span>
                  <span style={{ display: 'inline-block', width: 10, height: 10, background: item.color, borderRadius: 2, marginRight: 8 }} />
                  {item.category}
                </span>
                <span>₹{item.amount.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 12, color: '#52c41a' }}>
            ✓ Your discretionary spending is exactly at 30%!
          </div>
        </>
      );

    case '12':
      // Split FD + MF - Networth comparison
      const splitNetworthData = [
        { year: 'Now', noInvest: 500000, fdOnly: 500000, mfOnly: 500000, split: 500000 },
        { year: '5Y', noInvest: 575000, fdOnly: 710000, mfOnly: 890000, split: 800000 },
        { year: '10Y', noInvest: 650000, fdOnly: 1010000, mfOnly: 1580000, split: 1295000 },
        { year: '15Y', noInvest: 725000, fdOnly: 1440000, mfOnly: 2850000, split: 2145000 },
        { year: '20Y', noInvest: 800000, fdOnly: 2050000, mfOnly: 5200000, split: 3625000 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Split ₹10,000: <strong>₹5K in FD + ₹5K in MF</strong>
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, background: '#ff4d4f', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 11 }}>No Invest</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, background: '#2f54eb', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 11 }}>FD Only</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, background: '#52c41a', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 11 }}>MF Only</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, background: '#722ed1', borderRadius: 2 }} />
              <Typography.Text style={{ fontSize: 11 }}>Split 50-50</Typography.Text>
            </div>
          </div>
          <ResponsiveContainer width='100%' height={200}>
            <LineChart data={splitNetworthData}>
              <XAxis dataKey='year' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Line type='monotone' dataKey='noInvest' stroke='#ff4d4f' strokeWidth={1} strokeDasharray='5 5' dot={false} name='No Investment' />
              <Line type='monotone' dataKey='fdOnly' stroke='#2f54eb' strokeWidth={2} dot={{ r: 3 }} name='FD Only' />
              <Line type='monotone' dataKey='mfOnly' stroke='#52c41a' strokeWidth={2} dot={{ r: 3 }} name='MF Only' />
              <Line type='monotone' dataKey='split' stroke='#722ed1' strokeWidth={3} dot={{ r: 4 }} name='Split 50-50' />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ background: '#f9f0ff', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              <strong>Split Strategy Benefits:</strong>
              <br />• FD provides stability and guaranteed returns
              <br />• MF offers higher growth potential
              <br />• Balanced risk-reward profile
            </Typography.Text>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#722ed1', fontWeight: 600 }}>
              Split approach: ₹36L in 20 years (Best of both worlds!)
            </Typography.Text>
          </div>
        </>
      );

    case '13':
      // Upcoming bills
      const billsData = [
        { name: 'Rent', amount: 25000, due: '5th Jan', status: 'upcoming' },
        { name: 'Electricity', amount: 2500, due: '10th Jan', status: 'upcoming' },
        { name: 'Internet', amount: 1200, due: '12th Jan', status: 'upcoming' },
        { name: 'Insurance', amount: 3300, due: '15th Jan', status: 'upcoming' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ fontSize: 24, fontWeight: 600 }}>₹32,000</Typography.Text>
            <br />
            <Typography.Text style={{ color: '#888' }}>due this month</Typography.Text>
          </div>
          {billsData.map((bill, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <Typography.Text strong>{bill.name}</Typography.Text>
                <br />
                <Typography.Text style={{ color: '#888', fontSize: 12 }}>Due: {bill.due}</Typography.Text>
              </div>
              <Typography.Text strong>₹{bill.amount.toLocaleString('en-IN')}</Typography.Text>
            </div>
          ))}
          <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              💡 Your current balance can cover all upcoming bills ✓
            </Typography.Text>
          </div>
        </>
      );

    case '14':
      // EMI overview
      const emiData = [
        { name: 'Home Loan', amount: 28000, remaining: '12 years' },
        { name: 'Car Loan', amount: 12000, remaining: '3 years' },
        { name: 'Personal Loan', amount: 5000, remaining: '1 year' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ fontSize: 24, fontWeight: 600 }}>₹45,000</Typography.Text>
            <br />
            <Typography.Text style={{ color: '#888' }}>monthly EMI payments</Typography.Text>
          </div>
          {emiData.map((emi, i) => (
            <div key={i} style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text strong>{emi.name}</Typography.Text>
                <Typography.Text strong>₹{emi.amount.toLocaleString('en-IN')}</Typography.Text>
              </div>
              <Typography.Text style={{ color: '#888', fontSize: 12 }}>
                Remaining: {emi.remaining}
              </Typography.Text>
            </div>
          ))}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Debt-to-Income Ratio</span>
              <span style={{ color: '#52c41a', fontWeight: 600 }}>28%</span>
            </div>
            <Progress percent={28} strokeColor='#52c41a' showInfo={false} />
            <Typography.Text style={{ color: '#888', fontSize: 12 }}>
              ✓ Healthy! Keep it below 35%
            </Typography.Text>
          </div>
        </>
      );

    case '15':
      // 50-30-20 rule
      const ruleData = [
        { name: 'Needs (50%)', value: 50, ideal: 50, color: '#52c41a' },
        { name: 'Wants (30%)', value: 30, ideal: 30, color: '#1890ff' },
        { name: 'Savings (20%)', value: 20, ideal: 20, color: '#722ed1' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Typography.Text style={{ fontSize: 16, color: '#666' }}>
              The 50-30-20 budget rule helps manage money effectively
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <PieChart>
              <Pie data={ruleData} dataKey='value' nameKey='name' cx='50%' cy='50%' innerRadius={50} outerRadius={70}>
                {ruleData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, background: '#f6ffed', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <Typography.Text strong style={{ color: '#52c41a' }}>50%</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 12 }}>Needs</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 10, color: '#888' }}>Rent, Food, Bills</Typography.Text>
              </div>
              <div style={{ flex: 1, background: '#e6f7ff', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <Typography.Text strong style={{ color: '#1890ff' }}>30%</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 12 }}>Wants</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 10, color: '#888' }}>Entertainment</Typography.Text>
              </div>
              <div style={{ flex: 1, background: '#f9f0ff', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <Typography.Text strong style={{ color: '#722ed1' }}>20%</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 12 }}>Savings</Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 10, color: '#888' }}>Investments</Typography.Text>
              </div>
            </div>
          </div>
        </>
      );

    case '16':
      // Peer MF Investment comparison
      const peerMFData = [
        { name: 'You', amount: 15000, fill: '#1890ff' },
        { name: 'Peers Avg', amount: 18000, fill: '#52c41a' },
        { name: 'Top 10%', amount: 25000, fill: '#722ed1' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Monthly MF investment in your salary bracket (₹60K-80K)
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <BarChart data={peerMFData} layout='vertical'>
              <XAxis type='number' tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v / 1000}K`} />
              <YAxis dataKey='name' type='category' tick={{ fontSize: 12 }} width={70} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Bar dataKey='amount' radius={[0, 4, 4, 0]}>
                {peerMFData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#e6f4ff', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Your Investment</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#1890ff' }}>₹15,000</Typography.Text>
            </div>
            <div style={{ background: '#f6ffed', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Peer Average</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#52c41a' }}>₹18,000</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#fa8c16', fontWeight: 600 }}>
              Increase by ₹3,000/month to match your peers!
            </Typography.Text>
          </div>
        </>
      );

    case '17':
      // Peer Savings Rate comparison
      const peerSavingsData = [
        { name: 'You', rate: 18, fill: '#1890ff' },
        { name: 'Peers Avg', rate: 22, fill: '#52c41a' },
        { name: 'Top 10%', rate: 30, fill: '#722ed1' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Savings rate comparison (% of income)
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <BarChart data={peerSavingsData}>
              <XAxis dataKey='name' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey='rate' radius={[4, 4, 0, 0]}>
                {peerSavingsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Your savings rate</span>
              <span style={{ fontWeight: 600, color: '#1890ff' }}>18%</span>
            </div>
            <Progress percent={81} strokeColor='#1890ff' showInfo={false} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Target: 22%</Typography.Text>
              <Typography.Text style={{ fontSize: 12, color: '#fa8c16' }}>4% more to go!</Typography.Text>
            </div>
          </div>
          <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              💡 <strong>Tip:</strong> Automate transfers to savings on salary day
            </Typography.Text>
          </div>
        </>
      );

    case '18':
      // Emergency Fund comparison
      const emergencyFundData = [
        { name: 'Have Fund', value: 85 },
        { name: 'No Fund', value: 15 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              People earning ₹60K-80K with emergency fund
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <PieChart>
              <Pie
                data={emergencyFundData}
                innerRadius={50}
                outerRadius={70}
                dataKey='value'
                startAngle={90}
                endAngle={-270}
              >
                <Cell fill='#52c41a' />
                <Cell fill='#f0f0f0' />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: -60, marginBottom: 20 }}>
            <Typography.Text style={{ fontSize: 24, fontWeight: 600 }}>85%</Typography.Text>
            <br />
            <Typography.Text style={{ color: '#888' }}>have emergency fund</Typography.Text>
          </div>
          <div style={{ background: '#fff7e6', padding: 12, borderRadius: 8, marginTop: 20 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
              What's an Emergency Fund?
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12, color: '#666' }}>
              3-6 months of expenses saved for unexpected events like job loss, medical emergencies, or urgent repairs.
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <div style={{ flex: 1, background: '#f6ffed', padding: 12, borderRadius: 8, textAlign: 'center' }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Recommended</Typography.Text>
              <br />
              <Typography.Text strong style={{ color: '#52c41a' }}>₹3-4 Lakhs</Typography.Text>
            </div>
            <div style={{ flex: 1, background: '#e6f4ff', padding: 12, borderRadius: 8, textAlign: 'center' }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Monthly Target</Typography.Text>
              <br />
              <Typography.Text strong style={{ color: '#1890ff' }}>₹10,000</Typography.Text>
            </div>
          </div>
        </>
      );

    case '19':
      // Dining comparison
      const diningData = [
        { name: 'You', amount: 4250, fill: '#52c41a' },
        { name: 'Peers Avg', amount: 5000, fill: '#fa8c16' },
        { name: 'Big Spenders', amount: 8000, fill: '#ff4d4f' },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Monthly dining out expenses in your peer group
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={180}>
            <BarChart data={diningData}>
              <XAxis dataKey='name' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${v / 1000}K`} />
              <Tooltip formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`} />
              <Bar dataKey='amount' radius={[4, 4, 0, 0]}>
                {diningData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16, textAlign: 'center' }}>
            <div style={{ background: '#f6ffed', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Your Spending</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#52c41a' }}>₹4,250</Typography.Text>
            </div>
            <div style={{ background: '#fff7e6', padding: '8px 16px', borderRadius: 8 }}>
              <Typography.Text style={{ fontSize: 12, color: '#888' }}>Peer Average</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#fa8c16' }}>₹5,000</Typography.Text>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#52c41a', fontWeight: 600 }}>
              Great job! You spend 15% less than peers on dining out
            </Typography.Text>
          </div>
          <div style={{ background: '#f6ffed', padding: 12, borderRadius: 8, marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 12 }}>
              💰 <strong>Annual savings:</strong> ₹9,000 compared to average peers!
            </Typography.Text>
          </div>
        </>
      );

    case '20':
      // Peer Stock Allocation
      const stockAllocationData = [
        { name: 'You', allocation: 20, fill: '#1890ff' },
        { name: 'Peers Avg', allocation: 25, fill: '#52c41a' },
        { name: 'Top Earners', allocation: 30, fill: '#722ed1' },
      ];
      const portfolioComparisonData = [
        { category: 'Stocks', you: 20, peers: 25, top: 30 },
        { category: 'MF', you: 35, peers: 30, top: 25 },
        { category: 'FD', you: 30, peers: 25, top: 20 },
        { category: 'Savings', you: 15, peers: 20, top: 25 },
      ];
      return (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Typography.Text style={{ color: '#666' }}>
              Stock allocation (% of investment portfolio)
            </Typography.Text>
          </div>
          <ResponsiveContainer width='100%' height={160}>
            <BarChart data={stockAllocationData}>
              <XAxis dataKey='name' tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey='allocation' radius={[4, 4, 0, 0]}>
                {stockAllocationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 16 }}>
            <Typography.Text strong style={{ fontSize: 13 }}>Portfolio Breakdown Comparison</Typography.Text>
            <div style={{ marginTop: 8 }}>
              {portfolioComparisonData.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 50, fontSize: 12 }}>{item.category}</span>
                  <div style={{ flex: 1, height: 16, background: '#f0f0f0', borderRadius: 4, position: 'relative' }}>
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${item.you}%`,
                      background: '#1890ff',
                      borderRadius: 4,
                      opacity: 0.7
                    }} />
                    <div style={{
                      position: 'absolute',
                      left: `${item.top}%`,
                      top: 0,
                      width: 2,
                      height: '100%',
                      background: '#722ed1'
                    }} />
                  </div>
                  <span style={{ width: 35, fontSize: 11, color: '#1890ff' }}>{item.you}%</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11 }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#1890ff', borderRadius: 2, marginRight: 4 }} />You</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 2, background: '#722ed1', marginRight: 4 }} />Top Earners</span>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Typography.Text style={{ fontSize: 14, color: '#fa8c16', fontWeight: 600 }}>
              Consider increasing stock allocation by 10% for higher growth
            </Typography.Text>
          </div>
        </>
      );

    default:
      return (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Typography.Text>Financial insight coming soon!</Typography.Text>
        </div>
      );
  }
};

const NudgeDetails: React.FC<{
  nudge: Nudge;
}> = ({ nudge }) => {
  return (
    <Card className={styles.nudge_details} bordered={false} title={getTitle(nudge.id)}>
      {getDetails(nudge.id)}
    </Card>
  );
};

export default NudgeDetails;
